import { useEffect, useRef } from 'react';

interface EmbedVideoPlayerProps {
  src: string;
  className?: string;
  autoPlay?: boolean;
  loop?: boolean;
  muted?: boolean;
  isPaused?: boolean;
}

export function EmbedVideoPlayer({ src, className = '', autoPlay = true, loop = true, muted = true, isPaused = false }: EmbedVideoPlayerProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const separator = src.includes('?') ? '&' : '?';
  const embedSrc = `${src}${separator}autoplay=${autoPlay}&loop=${loop}&muted=${muted}&showPlayButton=false&showControls=false&responsive=true&preload=true`;

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe?.contentWindow) return;
    const command = isPaused ? 'pause' : 'play';
    iframe.contentWindow.postMessage({ event: command }, '*');
  }, [isPaused]);

  // Listen for video end events and restart if looping
  useEffect(() => {
    if (!loop) return;
    const handleMessage = (e: MessageEvent) => {
      if (e.data?.event === 'videoEnd' || e.data?.event === 'ended' || e.data?.type === 'ended') {
        const iframe = iframeRef.current;
        if (iframe?.contentWindow) {
          iframe.contentWindow.postMessage({ event: 'play' }, '*');
        }
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [loop]);

  return (
    <div className={`${className} relative`} data-testid="workout-video-player">
      {/* Scale iframe to 130% to fill container height and crop black side borders */}
      <iframe
        ref={iframeRef}
        src={embedSrc}
        className="absolute border-0"
        style={{
          top: '50%',
          left: '50%',
          width: '130%',
          height: '130%',
          transform: 'translate(-50%, -50%)',
        }}
        allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture"
        allowFullScreen
      />
    </div>
  );
}
